import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Save, Upload, Image as ImageIcon, X, RefreshCw, Plus,
  Pencil, Trash2, ExternalLink, Search, ArrowLeft, Eye, EyeOff, Star,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import AdminNav from "@/components/AdminNav";
import { resizeImage } from "@/lib/gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); } catch { return "-"; }
}

const emptyForm = {
  id: "",
  title: "",
  slug: "",
  cover_image: "",
  excerpt: "",
  content: "",
  author: "",
  category: "",
  tags: [],
  featured: false,
  published: true,
};

const SUGGESTED_CATEGORIES = [
  "Tahfizh",
  "Kajian & Ilmu",
  "Kegiatan Santri",
  "Kabar Pondok",
  "Kisah Alumni",
  "Ceramah",
  "Lainnya",
];

export default function AdminArtikel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // form object or null
  const [confirmDel, setConfirmDel] = useState(null); // article object

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/articles`, { headers: authHeader() });
      setRows(data || []);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/login", { replace: true }); }
      else toast.error("Gagal memuat artikel");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.title + " " + r.slug + " " + r.author).toLowerCase().includes(s));
  }, [rows, q]);

  const openCreate = () => setEditing({ ...emptyForm, author: user?.name || "Admin" });

  const openEdit = async (article) => {
    // fetch full content
    try {
      const { data } = await axios.get(`${API}/admin/articles/${article.id}`, { headers: authHeader() });
      setEditing({ ...emptyForm, ...data });
    } catch {
      toast.error("Gagal memuat artikel");
    }
  };

  const handleSaved = (saved) => {
    setEditing(null);
    // If this article is now featured, unset featured on all others locally
    setRows((prev) => {
      const light = { ...saved };
      delete light.content;
      const idx = prev.findIndex((r) => r.id === saved.id);
      let next;
      if (idx >= 0) {
        next = prev.slice();
        next[idx] = light;
      } else {
        next = [light, ...prev];
      }
      if (saved.featured) {
        next = next.map((r) => (r.id === saved.id ? r : { ...r, featured: false }));
      }
      return next;
    });
  };

  const doDelete = async (article) => {
    try {
      await axios.delete(`${API}/admin/articles/${article.id}`, { headers: authHeader() });
      setRows((prev) => prev.filter((r) => r.id !== article.id));
      toast.success("Artikel dihapus");
    } catch {
      toast.error("Gagal menghapus");
    } finally { setConfirmDel(null); }
  };

  return (
    <div className="min-h-screen bg-parchment text-forest">
      <AdminNav user={user} active="artikel" />
      <main className="max-w-[1400px] mx-auto pt-28 pb-16 px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold">Konten Landing Page</p>
            <h1 className="mt-1 font-serif-display text-forest text-3xl md:text-4xl">Kelola Artikel</h1>
            <p className="mt-1 text-sm text-moss max-w-2xl">
              Tambah, sunting, atau hapus artikel yang tampil di menu <em>Artikel</em> pada halaman awal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-moss" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari judul / penulis…"
                className="pl-9 pr-4 py-2 rounded-full border border-gold/40 bg-white text-sm w-64 focus:outline-none focus:border-forest"
                data-testid="artikel-search"
              />
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold/20"
              data-testid="artikel-reload"
            >
              <RefreshCw size={14} /> Muat ulang
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors"
              data-testid="artikel-new"
            >
              <Plus size={14} /> Artikel Baru
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-moss text-sm">
            <Loader2 className="animate-spin mr-2" size={16} /> Memuat artikel…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gold/25">
            <p className="text-moss text-sm">
              {rows.length === 0
                ? "Belum ada artikel. Klik 'Artikel Baru' untuk membuat yang pertama."
                : "Tidak ada artikel yang cocok dengan pencarian."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="artikel-list">
            {filtered.map((a) => (
              <ArticleRow key={a.id} article={a} onEdit={() => openEdit(a)} onDelete={() => setConfirmDel(a)} />
            ))}
          </div>
        )}
      </main>

      <AnimatePresence>
        {editing && (
          <ArticleForm
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={handleSaved}
          />
        )}
        {confirmDel && (
          <ConfirmDelete
            article={confirmDel}
            onCancel={() => setConfirmDel(null)}
            onConfirm={() => doDelete(confirmDel)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ArticleRow({ article, onEdit, onDelete }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex flex-col rounded-2xl overflow-hidden border border-gold/25 bg-white"
      data-testid={`artikel-row-${article.slug}`}
    >
      <div className="relative aspect-[16/9] bg-sand">
        {article.cover_image ? (
          <img src={article.cover_image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-moss/60"><ImageIcon size={26} /></div>
        )}
        <span
          className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-wider uppercase ${
            article.published ? "bg-forest text-parchment" : "bg-sand text-forest border border-gold/40"
          }`}
        >
          {article.published ? <Eye size={10} /> : <EyeOff size={10} />}
          {article.published ? "Terbit" : "Draft"}
        </span>
        {article.featured && (
          <span
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold text-forest text-[10px] tracking-wider uppercase font-semibold"
            data-testid={`artikel-badge-featured-${article.slug}`}
          >
            <Star size={10} /> Unggulan
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold">/{article.slug}</p>
        <h3 className="mt-1 font-serif-display text-forest text-lg leading-tight line-clamp-2">{article.title}</h3>
        {(article.category || (article.tags && article.tags.length > 0)) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {article.category && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/25 text-forest border border-gold/40">
                {article.category}
              </span>
            )}
            {(article.tags || []).slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-sand text-forest border border-gold/30">
                #{t}
              </span>
            ))}
            {(article.tags || []).length > 3 && (
              <span className="text-[10px] text-moss">+{article.tags.length - 3}</span>
            )}
          </div>
        )}
        {article.excerpt && <p className="mt-2 text-xs text-moss line-clamp-2">{article.excerpt}</p>}
        <p className="mt-3 text-[10px] text-moss">
          {article.author || "-"} · {fmt(article.updated_at || article.created_at)}
        </p>
        <div className="mt-4 pt-3 border-t border-gold/20 flex items-center gap-2">
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-forest text-parchment text-[11px] hover:bg-gold"
            data-testid={`artikel-edit-${article.slug}`}
          >
            <Pencil size={11} /> Sunting
          </button>
          <a
            href={`/artikel/${article.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gold/40 text-forest text-[11px] hover:bg-gold/20"
            data-testid={`artikel-open-${article.slug}`}
          >
            <ExternalLink size={11} /> Buka
          </a>
          <button
            onClick={onDelete}
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-[11px] hover:bg-red-50"
            data-testid={`artikel-delete-${article.slug}`}
          >
            <Trash2 size={11} /> Hapus
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function slugify(text) {
  return (text || "").toString().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ArticleForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(Boolean(initial?.slug));
  const isEdit = Boolean(form.id);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onTitleChange = (v) => {
    update({ title: v, slug: slugTouched ? form.slug : slugify(v) });
  };

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 2) {
      toast.error("Judul harus diisi (min 2 karakter)");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        slug: form.slug.trim() || slugify(form.title),
        cover_image: form.cover_image || "",
        excerpt: form.excerpt || "",
        content: form.content || "",
        author: form.author || "",
        category: (form.category || "").trim(),
        tags: Array.isArray(form.tags) ? form.tags : [],
        featured: !!form.featured,
        published: !!form.published,
      };
      const { data } = isEdit
        ? await axios.patch(`${API}/admin/articles/${form.id}`, body, { headers: authHeader() })
        : await axios.post(`${API}/admin/articles`, body, { headers: authHeader() });
      toast.success(isEdit ? "Artikel diperbarui" : "Artikel dibuat");
      onSaved(data);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Gagal menyimpan"));
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 md:p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-4xl max-h-[92vh] bg-parchment rounded-2xl border border-gold/40 overflow-hidden flex flex-col"
        data-testid="artikel-form"
      >
        <div className="shrink-0 px-6 md:px-8 py-4 border-b border-gold/25 flex flex-wrap items-center justify-between gap-3 bg-white/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-forest hover:text-gold" data-testid="artikel-form-close">
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold">
                {isEdit ? "Sunting Artikel" : "Artikel Baru"}
              </p>
              <p className="font-serif-display text-forest text-xl leading-tight">
                {form.title || "Tanpa Judul"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-moss cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.published}
                onChange={(e) => update({ published: e.target.checked })}
                data-testid="artikel-form-published"
              />
              Terbitkan
            </label>
            <label
              className={`inline-flex items-center gap-2 text-xs cursor-pointer px-2 py-1 rounded-full border transition-colors ${
                form.featured
                  ? "bg-gold text-forest border-gold"
                  : "text-moss border-gold/40 hover:bg-gold/10"
              }`}
              title="Hanya satu artikel yang bisa aktif sebagai Unggulan. Mengaktifkan ini akan otomatis menonaktifkan artikel unggulan sebelumnya."
            >
              <input
                type="checkbox"
                checked={!!form.featured}
                onChange={(e) => update({ featured: e.target.checked })}
                className="hidden"
                data-testid="artikel-form-featured"
              />
              <Star size={12} fill={form.featured ? "currentColor" : "none"} />
              Unggulan
            </label>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold disabled:opacity-60"
              data-testid="artikel-form-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Simpan
            </button>
          </div>
        </div>

        <div className="px-6 md:px-8 py-6 space-y-6 overflow-y-auto flex-1">
          <CoverUploader value={form.cover_image} onChange={(v) => update({ cover_image: v })} />

          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Judul</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              maxLength={200}
              placeholder="Judul artikel"
              className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-3 text-lg font-serif-display focus:outline-none focus:border-forest"
              data-testid="artikel-form-title"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Slug (URL)</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); update({ slug: slugify(e.target.value) }); }}
                maxLength={200}
                placeholder="slug-artikel"
                className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-forest font-mono"
                data-testid="artikel-form-slug"
              />
              <p className="mt-1 text-[10px] text-moss">URL: /artikel/{form.slug || "…"}</p>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Penulis</label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => update({ author: e.target.value })}
                maxLength={100}
                placeholder="Nama penulis / Admin Pondok"
                className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-forest"
                data-testid="artikel-form-author"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Ringkasan / Excerpt</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => update({ excerpt: e.target.value })}
              rows={2}
              maxLength={500}
              placeholder="Kalimat pengantar singkat yang tampil di daftar & bagian atas artikel."
              className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-3 text-sm focus:outline-none focus:border-forest"
              data-testid="artikel-form-excerpt"
            />
            <p className="mt-1 text-[10px] text-moss">{form.excerpt.length}/500 karakter</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Kategori</label>
              <input
                type="text"
                value={form.category || ""}
                onChange={(e) => update({ category: e.target.value.slice(0, 60) })}
                list="artikel-category-suggestions"
                maxLength={60}
                placeholder="Pilih atau ketik kategori"
                className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-forest"
                data-testid="artikel-form-category"
              />
              <datalist id="artikel-category-suggestions">
                {SUGGESTED_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
              <p className="mt-1 text-[10px] text-moss">Satu kategori utama per artikel (opsional).</p>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Tag</label>
              <TagsInput
                value={form.tags || []}
                onChange={(next) => update({ tags: next })}
              />
              <p className="mt-1 text-[10px] text-moss">Tekan Enter atau koma untuk menambah tag. Maksimum 20 tag.</p>
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Isi Artikel</label>
            <textarea
              value={form.content}
              onChange={(e) => update({ content: e.target.value })}
              rows={18}
              maxLength={200000}
              placeholder={"Tulis isi artikel di sini.\n\nPisahkan paragraf dengan baris kosong.\n\n## Sub-judul dengan dua pagar\n### Sub-judul kecil dengan tiga pagar\n> Kutipan diawali dengan tanda >"}
              className="mt-2 w-full rounded-xl border border-gold/30 bg-white px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-forest font-mono"
              data-testid="artikel-form-content"
            />
            <p className="mt-1 text-[10px] text-moss">
              Tips: baris kosong = paragraf baru · <code>## Judul</code> · <code>### Sub</code> · <code>&gt; Kutipan</code>
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CoverUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error("Format JPG/PNG/WEBP"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Maksimal 8MB"); return; }
    setBusy(true);
    try {
      const dataUrl = await resizeImage(file, 1600);
      onChange(dataUrl);
    } catch { toast.error("Gagal memproses gambar"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Gambar Sampul (opsional)</label>
      <div className="mt-2 relative w-full aspect-[16/8] rounded-xl overflow-hidden border-2 border-dashed border-gold/40 bg-sand">
        {value ? (
          <img src={value} alt="Cover" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-moss">
            <ImageIcon size={24} />
            <span className="text-xs mt-2">Belum ada gambar sampul</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="artikel-cover"
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-forest text-parchment text-xs hover:bg-gold disabled:opacity-60"
          data-testid="artikel-cover-pick"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {value ? "Ganti" : "Unggah"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs hover:bg-red-50"
            data-testid="artikel-cover-clear"
          >
            <X size={12} /> Hapus
          </button>
        )}
      </div>
    </div>
  );
}

function TagsInput({ value, onChange }) {
  const [input, setInput] = useState("");
  const list = Array.isArray(value) ? value : [];

  const add = (raw) => {
    const items = String(raw || "").split(",").map((t) => t.trim()).filter(Boolean);
    if (items.length === 0) return;
    const next = [...list];
    const seen = new Set(next.map((t) => t.toLowerCase()));
    for (const it of items) {
      const key = it.toLowerCase();
      if (seen.has(key)) continue;
      if (next.length >= 20) break;
      seen.add(key);
      next.push(it.slice(0, 40));
    }
    onChange(next);
    setInput("");
  };

  const removeAt = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && list.length > 0) {
      removeAt(list.length - 1);
    }
  };

  return (
    <div className="mt-2" data-testid="artikel-form-tags-wrapper">
      <div className="min-h-[42px] w-full rounded-xl border border-gold/30 bg-white px-3 py-2 flex flex-wrap items-center gap-1.5 focus-within:border-forest">
        {list.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-forest text-parchment text-[11px]"
            data-testid={`artikel-form-tag-chip-${i}`}
          >
            {t}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="hover:text-gold"
              aria-label={`Hapus tag ${t}`}
              data-testid={`artikel-form-tag-remove-${i}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => input.trim() && add(input)}
          placeholder={list.length === 0 ? "Ketik tag lalu Enter…" : ""}
          className="flex-1 min-w-[120px] bg-transparent text-sm focus:outline-none py-1"
          data-testid="artikel-form-tag-input"
        />
      </div>
    </div>
  );
}


function ConfirmDelete({ article, onCancel, onConfirm }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="w-full max-w-md bg-parchment rounded-2xl border border-gold/40 p-6"
        data-testid="artikel-delete-confirm"
      >
        <h3 className="font-serif-display text-forest text-xl">Hapus artikel?</h3>
        <p className="mt-2 text-sm text-moss">
          Artikel <strong className="text-forest">&ldquo;{article.title}&rdquo;</strong> akan dihapus permanen dan tidak dapat dikembalikan.
        </p>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-5 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold/20" data-testid="artikel-delete-cancel">Batal</button>
          <button onClick={onConfirm} className="px-5 py-2 rounded-full bg-red-700 text-parchment text-xs hover:bg-red-800" data-testid="artikel-delete-yes">Ya, hapus</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
